// HTML Page Viewer Modal - Shows extracted HTML content with chunk list

import { useState } from 'react';
import { Button, StatusBadge } from '../common';
import type { HtmlPageDetail, HtmlPageChunk } from '../../types/api';
import styles from './HtmlPageViewerModal.module.css';

interface HtmlPageViewerModalProps {
  page: HtmlPageDetail;
  onClose: () => void;
}

export function HtmlPageViewerModal({ page, onClose }: HtmlPageViewerModalProps) {
  const [selectedChunk, setSelectedChunk] = useState<HtmlPageChunk | null>(null);
  const [hoveredChunk, setHoveredChunk] = useState<string | null>(null);
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const [chunkViewMode, setChunkViewMode] = useState<Record<string, 'summary' | 'raw'>>({});
  const [contentTab, setContentTab] = useState<'sections' | 'tables'>('sections');

  // Toggle chunk expansion
  const toggleChunkExpanded = (chunkId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedChunks(prev => {
      const next = new Set(prev);
      if (next.has(chunkId)) {
        next.delete(chunkId);
      } else {
        next.add(chunkId);
      }
      return next;
    });
  };

  // Toggle chunk view mode (summary vs raw)
  const setChunkView = (chunkId: string, mode: 'summary' | 'raw', e: React.MouseEvent) => {
    e.stopPropagation();
    setChunkViewMode(prev => ({ ...prev, [chunkId]: mode }));
  };

  const handleChunkClick = (chunk: HtmlPageChunk) => {
    setSelectedChunk(chunk);
  };

  // Get status badge variant
  const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' => {
    switch (status) {
      case 'indexed':
        return 'success';
      case 'failed':
        return 'error';
      case 'crawled':
      case 'extracted':
        return 'info';
      default:
        return 'warning';
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{page.title}</h2>
            <div className={styles.urlRow}>
              <a
                href={page.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.pageUrl}
                onClick={(e) => e.stopPropagation()}
              >
                {page.url}
              </a>
              <StatusBadge variant={getStatusVariant(page.processing_status)}>
                {page.processing_status}
              </StatusBadge>
              <span className={styles.pageType}>{page.page_type}</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className={styles.body}>
          {/* Content Panel - Shows extracted text */}
          <div className={styles.contentPanel}>
            {/* Content Tabs */}
            <div className={styles.contentControls}>
              <div className={styles.contentTabs}>
                <button
                  className={`${styles.tabButton} ${contentTab === 'sections' ? styles.activeTab : ''}`}
                  onClick={() => setContentTab('sections')}
                >
                  Sections ({page.sections?.length || 0})
                </button>
                <button
                  className={`${styles.tabButton} ${contentTab === 'tables' ? styles.activeTab : ''}`}
                  onClick={() => setContentTab('tables')}
                >
                  Tables ({page.tables?.length || 0})
                </button>
              </div>
              {page.breadcrumb && (
                <div className={styles.breadcrumb}>{page.breadcrumb}</div>
              )}
            </div>

            {/* Content Area */}
            <div className={styles.contentArea}>
              {contentTab === 'sections' ? (
                page.sections && page.sections.length > 0 ? (
                  <div className={styles.sectionsList}>
                    {page.sections.map((section, idx) => (
                      <div key={idx} className={styles.section}>
                        {section.heading && (
                          <h3
                            className={styles.sectionHeading}
                            style={{ marginLeft: `${(section.level - 1) * 12}px` }}
                          >
                            {section.heading}
                          </h3>
                        )}
                        <div className={styles.sectionContent}>
                          {section.content}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noContent}>
                    <p>No sections extracted from this page.</p>
                    {page.description && (
                      <div className={styles.fallbackDescription}>
                        <strong>Description:</strong>
                        <p>{page.description}</p>
                      </div>
                    )}
                  </div>
                )
              ) : (
                page.tables && page.tables.length > 0 ? (
                  <div className={styles.tablesList}>
                    {page.tables.map((table, idx) => (
                      <div key={idx} className={styles.tableItem}>
                        <div className={styles.tableHeader}>Table {idx + 1}</div>
                        <pre className={styles.tableContent}>{table}</pre>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noContent}>
                    <p>No tables extracted from this page.</p>
                  </div>
                )
              )}
            </div>
          </div>

          {/* Chunks Panel */}
          <div className={styles.chunksPanel}>
            <div className={styles.chunksPanelHeader}>
              <h3>Chunks ({page.chunks.length})</h3>
              <div className={styles.chunkStats}>
                <span>{page.chunk_summary.with_summary} summarized</span>
                <span>{page.chunk_summary.useless} useless</span>
                <span>{page.chunk_summary.indexed} indexed</span>
              </div>
            </div>

            <div className={styles.chunksList}>
              {page.chunks.map((chunk) => {
                const isExpanded = expandedChunks.has(chunk.id);
                const viewMode = chunkViewMode[chunk.id] || 'summary';

                return (
                  <div
                    key={chunk.id}
                    id={`chunk-${chunk.id}`}
                    className={`${styles.chunkItem} ${
                      selectedChunk?.id === chunk.id ? styles.selectedChunk : ''
                    } ${hoveredChunk === chunk.id ? styles.hoveredChunk : ''} ${
                      isExpanded ? styles.expandedChunk : ''
                    }`}
                    onClick={() => handleChunkClick(chunk)}
                    onMouseEnter={() => setHoveredChunk(chunk.id)}
                    onMouseLeave={() => setHoveredChunk(null)}
                  >
                    {/* Compact Header */}
                    <div className={styles.chunkHeader}>
                      <div className={styles.chunkHeaderLeft}>
                        <span className={styles.chunkIndex}>#{chunk.chunk_index}</span>
                        <span className={styles.chunkMeta}>
                          {chunk.content_type} | {chunk.token_count} tok
                        </span>
                        {chunk.chunk_role && (
                          <span className={styles.roleTag}>{chunk.chunk_role}</span>
                        )}
                      </div>
                      <div className={styles.chunkHeaderRight}>
                        {chunk.is_useless && (
                          <StatusBadge variant="warning">Skip</StatusBadge>
                        )}
                        {chunk.vertex_doc_id && (
                          <StatusBadge variant="success">Indexed</StatusBadge>
                        )}
                        <button
                          className={styles.expandButton}
                          onClick={(e) => toggleChunkExpanded(chunk.id, e)}
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                      </div>
                    </div>

                    {/* Section Context */}
                    {chunk.heading_context && (
                      <div className={styles.chunkHeading} title={chunk.heading_context}>
                        {chunk.heading_context.length > 60
                          ? chunk.heading_context.substring(0, 60) + '...'
                          : chunk.heading_context}
                      </div>
                    )}

                    {/* Collapsed View: Show preview */}
                    {!isExpanded && (
                      <div className={styles.chunkPreview}>
                        {(chunk.summary || chunk.chunk_text).substring(0, 150)}
                        {(chunk.summary || chunk.chunk_text).length > 150 && '...'}
                      </div>
                    )}

                    {/* Expanded View: Tabs + Full Content */}
                    {isExpanded && (
                      <div className={styles.chunkExpanded}>
                        {/* View Mode Tabs */}
                        <div className={styles.chunkTabs}>
                          <button
                            className={`${styles.chunkTab} ${viewMode === 'summary' ? styles.activeChunkTab : ''}`}
                            onClick={(e) => setChunkView(chunk.id, 'summary', e)}
                          >
                            Summary
                          </button>
                          <button
                            className={`${styles.chunkTab} ${viewMode === 'raw' ? styles.activeChunkTab : ''}`}
                            onClick={(e) => setChunkView(chunk.id, 'raw', e)}
                          >
                            Raw Text
                          </button>
                        </div>

                        {/* Content Area */}
                        <div className={styles.chunkContent}>
                          {viewMode === 'summary' ? (
                            <div className={styles.chunkSummaryFull}>
                              {chunk.summary || <span className={styles.noSummary}>No summary available</span>}
                            </div>
                          ) : (
                            <pre className={styles.chunkRawText}>{chunk.chunk_text}</pre>
                          )}
                        </div>

                        {/* Metadata Footer */}
                        <div className={styles.chunkFooter}>
                          <span>Tokens: {chunk.token_count}</span>
                          {chunk.summary_tokens > 0 && <span>Summary: {chunk.summary_tokens} tok</span>}
                          {chunk.vertex_doc_id && (
                            <span title={chunk.vertex_doc_id}>
                              Vertex ID: {chunk.vertex_doc_id.substring(0, 8)}...
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
